import { useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { PageLayout } from '../../PageLayout';
import { PageCard } from '../../Compounds/CardWithTitle';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { SupplierFormDialog } from '../../Compounds/Advise/SupplierFormDialog';
import { ConfirmDialog } from '../../Compounds/Advise/ConfirmDialog';
import { SupplierGridColumnDefs } from './SupplierGridColumnDefs';
import { AdviseSupplier } from '../../../types/advise';
import {
  getAdviseSupplierSubject,
  getAdviseSupplierLoadingSubject,
  useAdviseSuppliers
} from '../../../store/AdviseSupplier';
import { hasAdviseFull } from '../../../store/User';
import { GridCellParams, GridSingleSelectColDef } from '@mui/x-data-grid-pro';
import { PermissibleRender } from '../../Compounds/PermissibleRender';
import { Permission } from '../../../store/GeneralStore';
import { AdviseSubNav } from '../../Compounds/Advise/AdviseSubNav';

export const AdviseSuppliersPage = () => {
  const [suppliers, setSuppliers] = useState<AdviseSupplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<AdviseSupplier | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdviseSupplier | null>(null);

  const { fetchSuppliers, createSupplier, updateSupplier, deleteSupplier } = useAdviseSuppliers();

  useEffect(() => {
    const supplierSub = getAdviseSupplierSubject().subscribe((data) => setSuppliers(data));
    const loadingSub = getAdviseSupplierLoadingSubject().subscribe((val) => setLoading(val));
    fetchSuppliers();
    return () => {
      supplierSub.unsubscribe();
      loadingSub.unsubscribe();
    };
  }, []);

  const handleCreate = () => {
    setEditingSupplier(null);
    setShowForm(true);
  };

  const handleEdit = (supplier: AdviseSupplier) => {
    setEditingSupplier(supplier);
    setShowForm(true);
  };

  const handleSave = async (supplierData: Partial<AdviseSupplier>) => {
    if (editingSupplier) {
      return updateSupplier(editingSupplier.id, supplierData);
    }
    return createSupplier(supplierData);
  };

  const handleDelete = async () => {
    if (deleteTarget) {
      await deleteSupplier(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  const canEdit = hasAdviseFull();

  const actionsColumn: GridSingleSelectColDef = {
    field: 'actions',
    headerName: '',
    sortable: false,
    width: 140,
    filterable: false,
    type: 'singleSelect',
    valueOptions: [],
    hideable: false,
    renderCell: (params: GridCellParams) => {
      if (!canEdit) return null;
      const row = params.row as AdviseSupplier;
      return (
        <div className="flex gap-2">
          <button
            className="text-sm text-blue-600 hover:underline"
            onClick={() => handleEdit(row)}>
            Edit
          </button>
          <button
            className="text-sm text-red-600 hover:underline"
            onClick={() => setDeleteTarget(row)}>
            Delete
          </button>
        </div>
      );
    }
  };

  const columns = canEdit ? [actionsColumn, ...SupplierGridColumnDefs] : SupplierGridColumnDefs;

  const pageActions = (
    <PermissibleRender requiredPermissions={[Permission.ADVISE_FULL]}>
      <Button
        variant="contained"
        size="small"
        onClick={handleCreate}>
        Add Supplier
      </Button>
    </PermissibleRender>
  );

  return (
    <PageLayout
      pageTitle="Advise Suppliers"
      pageActions={pageActions}
      dataCy="advise-suppliers-page">
      <AdviseSubNav />
      <PageCard
        title="Suppliers"
        childrenWrapperClass="min-h-80 min-w-full">
        <UpstackDataGrid
          dataCy="advise-suppliers-grid"
          title="Suppliers"
          rows={suppliers}
          columns={columns}
          loadingData={loading}
          page={'advise_supplier_grid'}
          gridSettings={{ muiConfig: {}, disabledFields: [] }}
          showSearch={true}
        />
      </PageCard>

      <SupplierFormDialog
        open={showForm}
        onClose={() => setShowForm(false)}
        onSave={handleSave}
        supplier={editingSupplier}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Supplier"
        message={`Are you sure you want to delete "${deleteTarget?.supplier_name}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </PageLayout>
  );
};
